import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MATERIALS_ORBITAL, MACHINES } from "@/i18n";
import { Gauge, Wind, Clock, Zap, Activity, Cpu, Loader2, AlertCircle, Database, Sparkles, Lock, ArrowRight, Ruler } from "lucide-react";
import { Link } from "react-router-dom";
import CalcActions from "@/components/CalcActions";
import CalcDisclaimer from "@/components/CalcDisclaimer";
import ProGate from "@/components/ProGate";
import GuestGate from "@/components/GuestGate";
import OrbitecElectrodeTable from "@/components/OrbitecElectrodeTable";
import OrbitecElectrodeRecommend from "@/components/OrbitecElectrodeRecommend";

// Static demo result shown to UNLOGGED guests after pressing "Vypočítať".
// Same shape as the real /api/calc/orbital response so the UI renders identically.
// CSS blur is applied on top → user sees "real-looking" output behind a gold paywall.
const DEMO_RESULT = {
  base_current_a: 78,
  travel_speed_mm_min: 105,
  estimated_weld_time_s: 90,
  rotations: 1.0,
  gas_type: "Ar 99.996",
  gas_flow_l_min: 12,
  back_purge_l_min: 5,
  tungsten: "Wolfram 2.4 mm · WC20 / WL15 · brús 30°",
  gas_pre_s: 6,
  gas_post_s: 12,
  fade_out_s: 8,
  notes: "Demo náhľad — reálne hodnoty pre tvoj DN a hrúbku po registrácii.",
  levels: [
    { level: 1, high_current_a: 82, low_current_a: 41, pulse_time_s: 0.25, base_time_s: 0.30, sector_time_s: 22, travel_speed_mm_min: 105, gas_flow_l_min: 12, back_purge_l_min: 5 },
    { level: 2, high_current_a: 78, low_current_a: 39, pulse_time_s: 0.25, base_time_s: 0.30, sector_time_s: 22, travel_speed_mm_min: 105, gas_flow_l_min: 12, back_purge_l_min: 5 },
    { level: 3, high_current_a: 74, low_current_a: 37, pulse_time_s: 0.25, base_time_s: 0.30, sector_time_s: 22, travel_speed_mm_min: 105, gas_flow_l_min: 12, back_purge_l_min: 5 },
    { level: 4, high_current_a: 70, low_current_a: 35, pulse_time_s: 0.25, base_time_s: 0.30, sector_time_s: 24, travel_speed_mm_min: 105, gas_flow_l_min: 12, back_purge_l_min: 5 },
  ],
};

export default function OrbitalCalc() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const isGuest = !user;
  const isFree = user && !user.is_subscribed && user.role !== "admin";
  const [params, setParams] = useSearchParams();
  const [machine, setMachine] = useState(params.get("machine") || "axxair");
  const [material, setMaterial] = useState(params.get("material") || "stainless_316");
  const [diameter, setDiameter] = useState(params.get("diameter") ? Number(params.get("diameter")) : 50);
  const [wall, setWall] = useState(params.get("wall_thickness") ? Number(params.get("wall_thickness")) : 2.0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldProgram, setFieldProgram] = useState(null);
  const [fieldLoading, setFieldLoading] = useState(false);
  const [fieldError, setFieldError] = useState("");
  // Orbitec tungsten-electrode length reference table (modal)
  const [electrodeTableOpen, setElectrodeTableOpen] = useState(false);

  const lookupField = async () => {
    setFieldLoading(true); setFieldError(""); setFieldProgram(null);
    try {
      const r = await api.post("/orbital/lookup", {
        d_mm: parseFloat(diameter),
        wall_mm: parseFloat(wall),
        material,
      });
      setFieldProgram(r.data);
    } catch (err) {
      setFieldError(err.response?.data?.detail || "Lookup failed");
    } finally { setFieldLoading(false); }
  };

  const doCompute = async (vals) => {
    setLoading(true); setError(""); setResult(null);
    // Guest mode: don't hit the API, show a fake-but-realistic blurred preview to demonstrate UX
    if (isGuest) {
      await new Promise((r) => setTimeout(r, 500));
      setResult(DEMO_RESULT);
      setLoading(false);
      return;
    }
    try {
      const r = await api.post("/calc/orbital", vals);
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Compute failed");
    } finally { setLoading(false); }
  };

  // Auto-compute on URL params (shared link)
  useEffect(() => {
    if (params.get("machine") || params.get("material") || params.get("diameter")) {
      doCompute({
        machine: params.get("machine") || machine,
        material: params.get("material") || material,
        diameter: parseFloat(params.get("diameter") || diameter),
        wall_thickness: parseFloat(params.get("wall_thickness") || wall),
      });
    }
    // eslint-disable-next-line
  }, []);

  const compute = async (e) => {
    e?.preventDefault();
    const vals = { machine, material, diameter: parseFloat(diameter), wall_thickness: parseFloat(wall) };
    setParams({ machine, material, diameter: String(diameter), wall_thickness: String(wall) }, { replace: true });
    await doCompute(vals);
  };

  return (
    <div data-testid="orbital-calc-page" className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="wb-tag !border-gold-600 !text-gold-500">ORBITAL</span>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">{t.orbital.title}</h1>
          <p className="text-sm text-zinc-500 font-mono uppercase tracking-wider mt-2">{t.orbital.sub}</p>
        </div>
        {!isGuest && !isFree && (
          <div className="flex flex-wrap gap-2 self-start mt-2">
            <button
              type="button"
              onClick={() => setElectrodeTableOpen(true)}
              data-testid="orbitec-electrode-table-btn"
              title={lang === "sk" ? "Nastavenia wolfrámovej elektródy pre hlavy Orbitec"
                : lang === "en" ? "Tungsten electrode settings for Orbitec heads"
                : lang === "de" ? "Wolframelektroden-Einstellung für Orbitec-Köpfe"
                : lang === "cs" ? "Nastavení wolframové elektrody pro hlavy Orbitec"
                : "Ustawienia elektrody wolframowej dla głowic Orbitec"}
              className="wb-btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs !border-gold-700 !text-gold-300 hover:!bg-gold-950/40 transition-colors"
            >
              <Ruler className="w-3.5 h-3.5 text-gold-500" />
              <span className="hidden sm:inline">
                {lang === "sk" ? "Dĺžka elektródy · Orbitec"
                  : lang === "en" ? "Electrode length · Orbitec"
                  : lang === "de" ? "Elektrodenlänge · Orbitec"
                  : lang === "cs" ? "Délka elektrody · Orbitec"
                  : "Długość elektrody · Orbitec"}
              </span>
              <span className="sm:hidden">
                {lang === "sk" ? "Dĺžka elektródy"
                  : lang === "en" ? "Electrode length"
                  : lang === "de" ? "Elektrodenlänge"
                  : lang === "cs" ? "Délka elektrody"
                  : "Długość elektrody"}
              </span>
            </button>
            <Link
              to="/programs/real"
              data-testid="orbital-real-programs-link"
              className="wb-btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs"
            >
              <Database className="w-3.5 h-3.5 text-gold-500" /> {t.realprog.title}
            </Link>
          </div>
        )}
      </div>

      {isGuest && (
        <div className="mb-8 max-w-2xl">
          <GuestGate />
        </div>
      )}

      {isFree && (
        <div className="mb-8 max-w-2xl">
          <ProGate message="Orbitálna kalkulačka (Axxair / Orbitec / Orbitalum) je súčasťou PRO predplatného. FREE režim ponúka len Manual TIG kalkulačku do DN50." />
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Inputs */}
        <form onSubmit={compute} className="lg:col-span-5 border border-zinc-800 bg-[var(--wb-surface)] p-6" data-testid="orbital-form">
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Cpu className="w-4 h-4 text-gold-500" /> Inputs
          </h2>

          <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.orbital.machine}</label>
          <div className="grid grid-cols-3 gap-px bg-zinc-800 mb-4" data-testid="machine-selector">
            {MACHINES.map((m) => (
              <button
                key={m.id} type="button"
                data-testid={`machine-${m.id}`}
                onClick={() => setMachine(m.id)}
                className={`py-2 text-sm font-display uppercase tracking-wider ${machine === m.id ? "bg-gold-600 text-white" : "bg-[var(--wb-bg)] text-zinc-400 hover:text-white"}`}
              >{m.name}</button>
            ))}
          </div>

          <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.orbital.material}</label>
          <select
            data-testid="material-select"
            value={material} onChange={(e) => setMaterial(e.target.value)}
            className="wb-input w-full px-3 py-2 text-sm mb-4"
          >
            {MATERIALS_ORBITAL.map((m) => (
              <option key={m.id} value={m.id}>{m[lang]}</option>
            ))}
          </select>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.orbital.diameter}</label>
              <input
                data-testid="diameter-input"
                type="number" step="0.1" min="1" required
                value={diameter} onChange={(e) => setDiameter(e.target.value)}
                className="wb-input w-full px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.orbital.wall}</label>
              <input
                data-testid="wall-input"
                type="number" step="0.1" min="0.1" required
                value={wall} onChange={(e) => setWall(e.target.value)}
                className="wb-input w-full px-3 py-2 text-sm font-mono"
              />
            </div>
          </div>

          <button
            data-testid="orbital-compute-btn"
            type="submit" disabled={loading}
            className="wb-btn-primary w-full py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.common.loading}</> : t.orbital.compute}
          </button>
          {error && error.startsWith("PRO_REQUIRED:") ? (
            <div className="mt-3"><ProGate compact message={error.replace("PRO_REQUIRED:", "").trim()} /></div>
          ) : error ? (
            <div data-testid="orbital-error" className="mt-3 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          ) : null}

          {/* Field-recorded program lookup — real-world user-submitted programs */}
          {!isFree && (
            <div className="mt-5 pt-5 border-t border-zinc-800" data-testid="field-lookup-block">
              <div className="flex items-start gap-2 mb-2">
                <Database className="w-4 h-4 text-gold-500 mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-mono uppercase tracking-wider text-gold-400">{t.orbital.fieldTitle}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">
                    {t.orbital.fieldDesc}
                  </div>
                </div>
              </div>
              <button type="button" onClick={lookupField} disabled={fieldLoading}
                data-testid="field-lookup-btn"
                className="wb-btn-ghost w-full py-2 text-xs flex items-center justify-center gap-2 disabled:opacity-50">
                {fieldLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {fieldLoading ? t.orbital.fieldSearching : t.orbital.fieldCta}
              </button>
              {fieldError && (
                <div className="mt-2 text-xs text-red-400 flex items-center gap-1.5"><AlertCircle className="w-3 h-3" /> {fieldError}</div>
              )}
              {fieldProgram && (
                <div className="mt-3 border border-gold-700/40 bg-gold-600/5 p-3 text-xs space-y-1.5" data-testid="field-program-result">
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold text-gold-300">{fieldProgram.field_program}</span>
                    <span className="text-[10px] font-mono uppercase text-zinc-500">{fieldProgram.source === "exact_match" ? t.orbital.exactMatch : t.orbital.interpolated}</span>
                  </div>
                  <div className="text-zinc-400 font-mono">
                    {fieldProgram.d_mm}×{fieldProgram.wall_mm}mm · {fieldProgram.travel_mm_min} mm/min · {fieldProgram.arc_voltage_v} V · pre-gas {fieldProgram.gas_pre_s}s
                  </div>
                  <div className="text-zinc-500 text-[10px]">{t.orbital.confidence}: {Math.round((fieldProgram.confidence || 0) * 100)}% · {t.orbital.sectorsLabel}: {fieldProgram.sectors.length}</div>
                  <table className="w-full mt-2 font-mono text-[10px]">
                    <thead><tr className="text-zinc-500"><th className="text-left">SEK</th><th className="text-right">HIGH A</th><th className="text-right">LOW A</th><th className="text-right">hi s</th><th className="text-right">lo s</th></tr></thead>
                    <tbody>{fieldProgram.sectors.map((s) => (
                      <tr key={s.nr} className="border-t border-zinc-800/60">
                        <td>{s.nr}</td><td className="text-right text-gold-400">{s.high_a}</td><td className="text-right">{s.low_a}</td><td className="text-right">{s.hi_s}</td><td className="text-right">{s.lo_s}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </form>

        {/* Result */}
        <div className="lg:col-span-7" data-testid="orbital-result-section">
          {!result ? (
            <div className="h-full border border-zinc-800 border-dashed flex flex-col items-center justify-center min-h-[400px] text-zinc-600">
              <Activity className="w-10 h-10 mb-3" strokeWidth={1.2} />
              <p className="font-mono text-xs uppercase tracking-wider">{t.orbital.waitingCompute}</p>
            </div>
          ) : (
            <div className="relative" data-testid={isGuest ? "orbital-result-block-guest" : "orbital-result-wrap"}>
              <div
                className={`space-y-px bg-zinc-800 ${isGuest ? "pointer-events-none select-none" : ""}`}
                style={isGuest ? { filter: "blur(7px)", WebkitFilter: "blur(7px)" } : undefined}
                aria-hidden={isGuest ? "true" : "false"}
                data-testid="orbital-result-block"
              >
              {/* Primary metrics row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800">
                <Metric icon={Zap} label={t.orbital.current} value={result.base_current_a} unit="A" testid="metric-current" />
                <Metric icon={Gauge} label={t.orbital.speed} value={result.travel_speed_mm_min} unit="mm/min" testid="metric-speed" />
                <Metric icon={Clock} label={t.orbital.time} value={result.estimated_weld_time_s} unit="s" testid="metric-time" />
                <Metric icon={Activity} label={t.orbital.rotations} value={result.rotations} unit="x" testid="metric-rotations" />
              </div>
              {/* Gas + tungsten */}
              <div className="grid md:grid-cols-3 gap-px bg-zinc-800">
                <InfoCell label={t.orbital.gas} value={result.gas_type} testid="info-gas" />
                <InfoCell label={t.orbital.gasFlow} value={`${result.gas_flow_l_min} l/min`} testid="info-gas-flow" />
                <InfoCell label={t.orbital.purge} value={`${result.back_purge_l_min} l/min`} testid="info-purge" />
              </div>
              <div className="bg-[var(--wb-surface)] p-5">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.orbital.tungsten}</div>
                <div className="text-sm">{result.tungsten}</div>
                {result.fade_out_s && (
                  <div className="mt-3 pt-3 border-t border-zinc-800 text-xs font-mono text-zinc-500">
                    {t.orbital.downSlope}: {result.fade_out_s}s · {t.orbital.gasPre}: {result.gas_pre_s}s · {t.orbital.gasPost}: {result.gas_post_s}s
                  </div>
                )}
                {/* Auto-recommended Orbitec electrode length for this pipe Ø */}
                <OrbitecElectrodeRecommend
                  pipeMm={parseFloat(diameter)}
                  machine={machine}
                  lang={lang}
                  onOpenTable={() => setElectrodeTableOpen(true)}
                />
              </div>

              {/* Sequence levels */}
              <div className="bg-[var(--wb-surface)] p-5">
                <h3 className="font-display font-bold text-lg mb-3">{t.orbital.levels}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm font-mono" data-testid="orbital-levels-table">
                    <thead>
                      <tr className="text-zinc-500 text-xs uppercase tracking-wider">
                        <th className="text-left py-2 pr-3">{t.orbital.level}</th>
                        <th className="text-right py-2 pr-3 text-gold-400">{t.orbital.highCurrent}</th>
                        <th className="text-right py-2 pr-3 text-zinc-400">{t.orbital.lowCurrent}</th>
                        <th className="text-right py-2 pr-3">{t.orbital.pulse}</th>
                        <th className="text-right py-2 pr-3">{t.orbital.base}</th>
                        <th className="text-right py-2 pr-3">{t.orbital.sectorTime}</th>
                        <th className="text-right py-2 pr-3">{t.orbital.travel}</th>
                        <th className="text-right py-2">{t.orbital.gasPurge}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.levels.map((l) => (
                        <tr key={l.level} className="border-t border-zinc-800">
                          <td className="py-2 pr-3 font-display font-bold">L{l.level}</td>
                          <td className="py-2 pr-3 text-right text-gold-400 font-bold">{l.high_current_a} A</td>
                          <td className="py-2 pr-3 text-right text-zinc-300">{l.low_current_a} A</td>
                          <td className="py-2 pr-3 text-right">{l.pulse_time_s} s</td>
                          <td className="py-2 pr-3 text-right">{l.base_time_s} s</td>
                          <td className="py-2 pr-3 text-right text-gold-300">{l.sector_time_s || "—"} s</td>
                          <td className="py-2 pr-3 text-right">{l.travel_speed_mm_min} mm/m</td>
                          <td className="py-2 text-right text-zinc-400">{l.gas_flow_l_min}/{l.back_purge_l_min} l/min</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-[var(--wb-surface)] p-4 text-xs text-zinc-500 font-mono">{result.notes}</div>
              {!isGuest && (
                <CalcActions
                  type="orbital"
                  inputs={{ machine, material, diameter, wall_thickness: wall }}
                  outputs={result}
                  shareParams={{ machine, material, diameter, wall_thickness: wall }}
                  title={`Orbital — ${machine.toUpperCase()} · ${material} · ⌀${diameter}×${wall}mm`}
                />
              )}
              </div>
              {isGuest && (
                <div
                  data-testid="orbital-guest-overlay"
                  className="absolute inset-0 flex items-center justify-center p-4"
                >
                  <div className="max-w-md border border-gold-600 bg-gradient-to-br from-zinc-950/95 via-zinc-900/95 to-black/95 backdrop-blur-sm shadow-[0_20px_60px_-10px_rgba(212,175,55,0.5)] p-6 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 border-2 border-gold-500 bg-gold-500/10 flex items-center justify-center">
                      <Lock className="w-7 h-7 text-gold-400" strokeWidth={1.5} />
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-[3px] text-gold-400 mb-2">UKÁŽKA · NÁHĽAD VÝSLEDKU</div>
                    <h3 className="font-display font-black text-2xl mb-2 leading-tight">
                      {lang === "en" ? "This is what real calculation looks like"
                        : lang === "de" ? "So sieht eine echte Berechnung aus"
                        : lang === "pl" ? "Tak wygląda prawdziwy wynik"
                        : lang === "cs" ? "Takhle vypadá skutečný výpočet"
                        : "Takto vyzerá skutočný výpočet"}
                    </h3>
                    <p className="text-sm text-zinc-400 mb-5 leading-relaxed">
                      {lang === "en" ? "Register for free and unlock the full table — Axxair / Orbitec / Orbitalum parameters precisely for YOUR pipe."
                        : lang === "de" ? "Kostenlos registrieren und die volle Tabelle freischalten — Axxair / Orbitec / Orbitalum Parameter exakt für DEIN Rohr."
                        : lang === "pl" ? "Zarejestruj się za darmo i odblokuj pełną tabelę — parametry Axxair / Orbitec / Orbitalum dokładnie dla TWOJEJ rury."
                        : lang === "cs" ? "Zaregistruj se zdarma a odemkni plnou tabulku — parametry Axxair / Orbitec / Orbitalum přesně pro TVOJI trubku."
                        : "Zaregistruj sa zadarmo a odomkni plnú tabuľku — parametre Axxair / Orbitec / Orbitalum presne pre TVOJU trubku."}
                    </p>
                    <Link
                      to="/register"
                      data-testid="orbital-guest-overlay-cta"
                      className="wb-btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm"
                    >
                      {lang === "en" ? "Sign up — it's free"
                        : lang === "de" ? "Kostenlos registrieren"
                        : lang === "pl" ? "Zarejestruj się za darmo"
                        : lang === "cs" ? "Zaregistrovat se zdarma"
                        : "Zaregistrovať sa zadarmo"}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <div className="mt-3">
                      <Link
                        to="/login"
                        data-testid="orbital-guest-overlay-login"
                        className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-gold-400"
                      >
                        {lang === "en" ? "Already a member — log in"
                          : lang === "de" ? "Schon dabei — anmelden"
                          : lang === "pl" ? "Mam konto — zaloguj się"
                          : lang === "cs" ? "Mám účet — přihlásit se"
                          : "Mám účet — prihlásiť sa"}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Persistent disclaimer — always visible (logged in or guest, before or after compute) */}
      <div className="mt-6">
        <CalcDisclaimer />
      </div>

      {/* Orbitec tungsten electrode reference table — opens as full-screen modal */}
      <OrbitecElectrodeTable
        open={electrodeTableOpen}
        onClose={() => setElectrodeTableOpen(false)}
        lang={lang}
      />
    </div>
  );
}

function Metric({ icon: Icon, label, value, unit, testid }) {
  return (
    <div className="bg-[var(--wb-surface)] p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</span>
        <Icon className="w-4 h-4 text-gold-500" strokeWidth={1.5} />
      </div>
      <div className="font-display font-black text-3xl text-white leading-none">{value}</div>
      <div className="text-xs font-mono text-zinc-500 mt-1">{unit}</div>
    </div>
  );
}

function InfoCell({ label, value, testid }) {
  return (
    <div className="bg-[var(--wb-surface)] p-5" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
